import {useLingui} from '@lingui/react/macro';
import {useEffect, Fragment} from 'react';
import {Svg, Path} from 'react-native-svg';
import {useCallback, useState} from 'react';
import {View, Text, Pressable, StyleSheet, TextInput as TextInputBase} from 'react-native';
import {useToast} from 'react-native-toast-notifications';
import {IconButton} from 'interface/base/IconButton';
import {TextInput} from 'interface/base/Input';
import {Switch} from 'interface/base/Switch';
import {configureFileDrop} from 'utils/mediafire';
import {colors, vectors} from 'config/styles';
import {theme} from 'config/themes';
import {isNative} from 'utils/platform';
import device from 'extensions/device';

export interface FileDropProps {
  id: string,
  name: string,
  isActive: boolean,
  primaryColor: string,
  exit: () => void,
}

export function ModalFileDrop(props: FileDropProps) {
  const {t} = useLingui();
  const [title, setTitle] = useState('');
  const [fullWidth, setFullWidth] = useState('');
  const [emailAlert, setEmailAlert] = useState('');
  const [hostedLink, setHostedLink] = useState('Loading...');
  const [embeddedCode, setEmbeddedCode] = useState('Loading...');
  const [isConfiguring, setConfiguring] = useState(false);
  const [isEnabled, setEnabled] = useState(props.isActive);
  const classes = {
    input: [
      styles.input,
      {borderColor: props.primaryColor},
    ],
  };

  const toast = useToast();
  
  const cancel = useCallback(() => setConfiguring(false), []);
  const configure = useCallback(() => setConfiguring(true), []);
  const pickWidth = useCallback((e: string) => setFullWidth(e), []);
  const pickEmail = useCallback((e: string) => setEmailAlert(e), []);

  const disable = useCallback(async () => {
    const res = await configureFileDrop('disable', props.id);
    if (res && res['result'] === 'Success') {
      toast.show(t`Disabled the “${name}” FileDrop`);
    } else {
      toast.show(res?.message ?? t`Failed to disable the FileDrop`, {type: 'danger'});
    }
    props.exit();
  }, [props.id]);

  const update = useCallback(async () => {
    const res = await configureFileDrop('enable', props.id, title, fullWidth, emailAlert);
    if (res && res['result'] === 'Success') {
      setEmbeddedCode(res.htmlEmbedCode);
      setHostedLink(res.hostedFileDrop);
      toast.show(t`Updated the “${name}” FileDrop`);
    } else {
      toast.show(res?.message ?? t`Failed to update the FileDrop`, {type: 'danger'});
    }
    if (isEnabled) {
      setConfiguring(false);
    } else {
      setEnabled(true);
    }
  }, [props.id, title, fullWidth, emailAlert]);

  const copyHostedLink = useCallback(() => {
    if (hostedLink !== 'Loading...') {
      device.clipboardSet(hostedLink);
      toast.show(t`Copied hosted filedrop link`);
    }
  }, [hostedLink]);

  const copyEmbedCode = useCallback(() => {
    if (embeddedCode !== 'Loading...') {
      device.clipboardSet(embeddedCode);
      toast.show(t`Copied embedded filedrop code`);
    }
  }, [embeddedCode]);

  useEffect(() => {
    configureFileDrop('view', props.id).then((res) => {
      setEmbeddedCode(res.htmlEmbedCode);
      setHostedLink(res.hostedFileDrop);
    });
  }, [props.id]);

  return (
    <View style={styles.root}>
      <View style={styles.header}>
        <Text numberOfLines={1} style={styles.title}>
          {t`Configure FileDrop`}
        </Text>
        <Pressable style={styles.close} onPress={props.exit}>
          <Svg viewBox="0 0 14 14" width={14} height={14}>
            <Path d={vectors.icons.close} fill={colors.neutral.mid}/>
          </Svg>
        </Pressable>
      </View>
      <View style={styles.content}>
        {!isEnabled &&
          <View>
            <Text style={styles.message}>
              {t`Would you like to enable this folder as a FileDrop? This action can be undone at anytime.`}
            </Text>
          </View>
        }
        {isEnabled && !isConfiguring &&
          <View>
            <Text style={styles.label}>
              {t`Share this link with friends to allow them to upload to this folder`}
            </Text>
            <View style={classes.input}>
              <TextInputBase
                multiline
                selectTextOnFocus
                showSoftInputOnFocus={false}
                selectionColor={theme.app.selection}
                onChange={() => false}
                style={styles.fields}
                value={hostedLink}
              />
              <Pressable style={styles.copyIcon} onPress={copyHostedLink}>
                <Svg viewBox="0 0 20 10" width={18} height={18}>
                  <Path d={vectors.icons.public} fill={colors.neutral.mid}/>
                </Svg>
              </Pressable>
            </View>
            <View style={styles.spacerV}/>
            <Text style={styles.label}>
              {t`To embed a filedrop, include the code below into your webpage`}
            </Text>
            <View style={classes.input}>
              <TextInputBase
                multiline
                selectTextOnFocus
                showSoftInputOnFocus={false}
                selectionColor={theme.app.selection}
                onChange={() => false}
                style={styles.fields}
                value={embeddedCode}
              />
              <Pressable style={styles.copyIcon} onPress={copyEmbedCode}>
                <Svg viewBox="0 0 20 10" width={18} height={18}>
                  <Path d={vectors.icons.public} fill={colors.neutral.mid}/>
                </Svg>
              </Pressable>
            </View>
          </View>
        }
        {isEnabled && isConfiguring &&
          <View>
            <Text style={styles.message}>
              {t`Message displayed in uploader`}
            </Text>
            <TextInput
              autoFocus
              onChangeText={setTitle}
              onSubmitEditing={update}
              placeholder={t`Upload to my MediaFire FileDrop`}
              placeholderTextColor={theme.dialog.input.placeholder}
            />
            <View style={styles.spacerV}/>
            <View style={styles.switchWrapper}>
              <Text style={styles.labelSwitch}>
                {t`Should the uploader extend to the width of the page?`}
              </Text>
              <Switch
                value={fullWidth === 'full'}
                onValueChange={(on) => pickWidth(on ? 'full' : 'yes')}
                ios_backgroundColor={theme.dialog.input.fill}
                trackColor={{false: theme.dialog.input.fill, true: colors.primary.brand}}
                thumbColor={colors.neutral.white}>
              </Switch>
            </View>
            <View style={styles.spacerV}/>
            <View style={styles.switchWrapper}>
              <Text style={styles.labelSwitch}>
                {t`Would you like to receive email notifications for uploads?`}
              </Text>
              <Switch
                value={emailAlert === 'yes'}
                onValueChange={(on) => pickEmail(on ? 'yes' : 'no')}
                ios_backgroundColor={theme.dialog.input.fill}
                trackColor={{false: theme.dialog.input.fill, true: colors.primary.brand}}
                thumbColor={colors.neutral.white}>
              </Switch>
            </View>
          </View>
        }
      </View>
      <View style={styles.buttons}>
        {!isEnabled &&
          <Fragment>
            <IconButton
              mode="primary"
              text={t`Enable`.toUpperCase()}
              onPress={update}
            />
            <View style={styles.spacerH}/>
            <IconButton
              mode="cancel"
              text={t`Cancel`.toUpperCase()}
              onPress={props.exit}
            />
          </Fragment>
        }
        {isEnabled && isConfiguring &&
          <Fragment>
            <IconButton
              mode="primary"
              text={t`Save`.toUpperCase()}
              onPress={update}
            />
            <View style={styles.spacerH}/>
            <IconButton
              mode="destructive"
              text={t`Disable`.toUpperCase()}
              onPress={disable}
            />
            <View style={styles.spacerH}/>
            <IconButton
              mode="cancel"
              text={t`Cancel`.toUpperCase()}
              onPress={cancel}
            />
          </Fragment>
        }
        {isEnabled && !isConfiguring &&
          <Fragment>
            <IconButton
              mode="primary"
              text={t`Configure`.toUpperCase()}
              onPress={configure}
            />
            <View style={styles.spacerH}/>
            <IconButton
              mode="cancel"
              text={t`Cancel`.toUpperCase()}
              onPress={props.exit}
            />
          </Fragment>
        }
      </View>
    </View>
  );
}

export const styles = StyleSheet.create({
  root: {
    flex: 1,
    justifyContent: isNative() ? undefined : 'space-between',
    backgroundColor: theme.dialog.fill,
  },
  spacerV: {
    height: 20,
  },
  spacerH: {
    width: 6,
  },
  content: {
    paddingHorizontal: 20,
  },
  switchWrapper: {
    flexDirection: 'column',
    alignItems: 'flex-start',
  },
  input: {
    height: 100,
    marginTop: 10,
    backgroundColor: colors.neutral.lightest,
  },
  buttons: {
    flexDirection: 'row-reverse',
    padding: 20,
  },
  copyIcon: {
    position: 'absolute',
    paddingHorizontal: 10,
    paddingVertical: 8,
    right: 0,
    bottom: 0,
  },
  fields: {
    flex: 1,
    padding: 10,
    borderRadius: 0,
    fontSize: 13,
    fontFamily: 'monospace',
    color: theme.dialog.input.placeholder,
    backgroundColor: theme.dialog.input.fill,
  },
  label: {
    fontSize: 14,
    color: theme.dialog.text,
  },
  labelSwitch: {
    fontSize: 14,
    color: theme.dialog.text,
    marginBottom: 10,
  },
  message: {
    fontSize: 14,
    color: theme.dialog.text,
    marginBottom: 10,
  },
  header: {
  },
  title: {
    fontSize: 20,
    fontWeight: '300',
    color: theme.dialog.title,
    lineHeight: 65,
    marginLeft: 19,
  },
  close: {
    position: 'absolute',
    alignItems: 'center',
    justifyContent: 'center',
    height: 65,
    width: 58,
    right: 1,
    top: -2,
  },
});
